import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const TEAM = [
  {
    id: 'ziga',
    name: 'Žiga Goltes',
    title: 'CEO',
    bio: 'Z več kot 15-letnimi izkušnjami v agencijskem svetu in vodenju kompleksnih BTL projektov ne gradi le procesov, temveč odnose. Kot soustvarjalec inovacij v Agenciji 101 je tlakoval pot modernim pristopom k promociji. Danes v okviru Wibe Solutions združuje moč novih tehnologij s pristno človeško komunikacijo — kreativne rešitve si zaslužijo brezhibno izvedbo, ki temelji na zaupanju in dobrem vibu ekipe.',
    img: '/team/ziga-goltes.jpg',
  },
  {
    id: 'andrej',
    name: 'Andrej Hrastar',
    title: 'Vodja projekta',
    bio: 'Strateški vodja z več kot 20-letnimi izkušnjami v avtomobilski industriji in prodajnem managementu. Svojo pot je gradil na vodenju ključnih projektov za priznane blagovne znamke, kot sta Porsche in VW, kjer se je specializiral za vzpostavitev e-mobilnosti in inovativnih go-to-market strategij. Verjame, da so za uspeh podjetja ključni ljudje in močna sinergija ekipe.',
    img: '/team/andrej-hrastar.jpg',
  },
  {
    id: 'karin',
    name: 'Karin Kočar',
    title: 'Recruitment & Back Office',
    bio: 'Z več kot 15-letno kilometrino v upravljanju hostesnih in promocijskih storitev je ena vodilnih strokovnjakinj za terensko kadrovanje v Sloveniji. Njena vloga pri projektu INFORMERS je ključna: zagotavljanje vrhunske selekcije informatorjev (21+), ki ustrezajo strogim standardom Philip Morrisa. Združuje disciplino pri vodenju s človeškim pristopom, kar se odraža v izjemni lojalnosti ekip in visoki kakovosti izvedbe na vsaki lokaciji.',
    img: '/team/karin-kocar.jpg',
  },
  {
    id: 'alen',
    name: 'Alen Radi',
    title: 'Operativa',
    bio: 'V svetu terenskih aktivacij je sinonim za operativno ostrino in zanesljivost. Kot desna roka vodstva prevzema odgovornost za tehnično in logistično stabilnost projektov, pri čemer s svojo prodornostjo izstopa kot eden najperspektivnejših posameznikov svoje generacije. Njegova vloga v ekipi Wibe je ključna za zagotavljanje kakovosti podatkov in nemotenega pretoka informacij, kar naročniku prinaša popolno varnost pri izvedbi.',
    img: '/team/alen-radi.jpg',
  },
];

const Avatar = ({ member }) => (
  <div className="relative w-24 h-24 mx-auto mb-5">
    {member.img ? (
      <img
        src={member.img}
        alt={member.name}
        className="w-24 h-24 rounded-full object-cover object-top border-2 border-[#C87533]/40"
        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
      />
    ) : null}
    {/* Fallback initials */}
    <div
      className="w-24 h-24 rounded-full bg-[#C87533]/15 border-2 border-[#C87533]/40 flex items-center justify-center"
      style={{ display: member.img ? 'none' : 'flex' }}
    >
      <span className="text-2xl font-bold text-[#C87533]">
        {member.name.split(' ').map(n => n[0]).join('')}
      </span>
    </div>
    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#C87533] flex items-center justify-center">
      <div className="w-2 h-2 rounded-full bg-white" />
    </div>
  </div>
);

export const TeamSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).team;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.team-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.15,
          scrollTrigger: { trigger: '.team-grid', start: 'top 80%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="team"
      ref={sectionRef}
      data-testid="team-section"
      className="scroll-section relative min-h-screen flex items-center overflow-hidden"
    >
      {/* subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-[#C87533]/5 blur-[80px]" />
      </div>

      <div className="section-content max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {tx.headline} <span className="text-copper-gradient">{tx.headlineAccent}</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base lg:text-lg">
            {tx.sub}
          </p>
        </div>

        {/* Cards */}
        <div className="team-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tx.members.map((member, idx) => {
            const imgMap = ['/team/ziga-goltes.jpg','/team/andrej-hrastar.jpg','/team/karin-kocar.jpg','/team/alen-radi.jpg'];
            const idMap  = ['ziga','andrej','karin','alen'];
            const m = { ...member, img: imgMap[idx], id: idMap[idx] };
            return (
              <div
                key={m.id}
                className="team-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 text-center hover:border-[#C87533]/30 hover:bg-white/5 transition-all duration-300"
              >
                <Avatar member={m} />
                <h3 className="font-display text-lg font-bold text-white mb-1">{m.name}</h3>
                <p className="font-mono text-xs text-[#C87533] uppercase tracking-wider mb-3">{m.title}</p>
                <p className="text-white/55 text-sm leading-relaxed">{m.bio}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
